import { createContext, useContext, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { ToastProvider } from './components/Toast';
import { TenantProvider } from './context/TenantContext';
import { useAuth } from './hooks/useAuth';
import AppShell from './components/AppShell';
import Login      from './pages/Login';
import Dashboard  from './pages/Dashboard';
import Tickets    from './pages/Tickets';
import NewTicket  from './pages/NewTicket';
import TicketDetail from './pages/TicketDetail';
import Approvals  from './pages/Approvals';
import Tasks      from './pages/Tasks';
import Reports    from './pages/Reports';
import Admin        from './pages/Admin';
import Employees     from './pages/admin/Employees';
import ApprovalChains from './pages/admin/ApprovalChains';
import Branding       from './pages/admin/Branding';
import AuditLog        from './pages/admin/AuditLog';
import CannedResponses from './pages/admin/CannedResponses';
import SlaPolicies     from './pages/admin/SlaPolicies';
import Notifications   from './pages/Notifications';
import KnowledgeBase   from './pages/KnowledgeBase';
import AllTickets   from './pages/AllTickets';
import AssignmentLog from './pages/AssignmentLog';
import AgentWorkspace from './pages/AgentWorkspace';
import AuthCallback  from './pages/AuthCallback';
import type { Role, User } from './types';
import { hasRole } from './utils/roleUtils';
import { rememberPostLoginRedirect } from './services/authService';

// Shares the already-resolved user from PrivateLayout so RoleGuard can read it
// synchronously without spawning its own useAuth() instance (which starts null).
const AuthCtx = createContext<User | null>(null);

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center text-sm text-gray-400">
    Loading…
  </div>
);

function PrivateLayout() {
  const { user, loading }        = useAuth();
  const isAuthenticated          = useIsAuthenticated();
  const { inProgress, instance } = useMsal();
  const location                 = useLocation();
  const msalPending              = inProgress !== InteractionStatus.None;

  // Our own session is gone (idle logout, failed refresh) but MSAL's cache still
  // reports an account — useAuth's logout() clears that cache too, so this is
  // normally just a one-render blip. If it isn't (some path nulled `user`
  // without going through logout()), force it after a short grace period
  // instead of spinning forever — that bug is exactly what this guards against.
  const stuck = !loading && !msalPending && !user && isAuthenticated;
  const [stuckTimedOut, setStuckTimedOut] = useState(false);

  useEffect(() => {
    if (!stuck) { setStuckTimedOut(false); return; }
    const t = setTimeout(() => {
      instance.clearCache().catch(() => {});
      setStuckTimedOut(true);
    }, 3000);
    return () => clearTimeout(t);
  }, [stuck, instance]);

  if (loading || msalPending) return <Spinner />;

  if (stuck && !stuckTimedOut) return <Spinner />;

  if (!user) {
    // Remember where this visit was actually headed (e.g. a ticket link from an
    // email notification) so the login flow can return here afterward instead
    // of always landing on the Dashboard — see authService.ts.
    rememberPostLoginRedirect(location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  return (
    <AuthCtx.Provider value={user}>
      <AppShell />
    </AuthCtx.Provider>
  );
}

function RoleGuard({ roles, children }: { roles: Role[]; children: JSX.Element }) {
  const user = useContext(AuthCtx);
  if (!user) return <Navigate to="/dashboard" replace />;
  if (roles.some(r => hasRole(user, r))) return children;
  return <Navigate to="/dashboard" replace />;
}

function AppInner() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login"         element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route element={<PrivateLayout />}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard"   element={<Dashboard />} />
          <Route path="/tickets"     element={<Tickets />} />
          <Route path="/tickets/new" element={<NewTicket />} />
          <Route path="/tickets/:id" element={<TicketDetail />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/kb" element={<KnowledgeBase />} />
          <Route path="/approvals"    element={
            <RoleGuard roles={['line_manager','top_management']}>
              <Approvals />
            </RoleGuard>
          } />
          <Route path="/all-tickets" element={
            <RoleGuard roles={['it_manager','admin']}>
              <AllTickets />
            </RoleGuard>
          } />
          <Route path="/assignment-log" element={
            <RoleGuard roles={['it_manager','admin']}>
              <AssignmentLog />
            </RoleGuard>
          } />
          <Route path="/workspace"   element={
            <RoleGuard roles={['it_staff','it_manager','admin']}>
              <AgentWorkspace />
            </RoleGuard>
          } />
          <Route path="/tasks"       element={
            <RoleGuard roles={['it_staff','it_manager']}>
              <Tasks />
            </RoleGuard>
          } />
          <Route path="/reports"     element={
            <RoleGuard roles={['it_manager']}>
              <Reports />
            </RoleGuard>
          } />
          <Route path="/admin"       element={
            <RoleGuard roles={['admin']}>
              <Admin />
            </RoleGuard>
          } />
          <Route path="/admin/employees" element={
            <RoleGuard roles={['admin']}>
              <Employees />
            </RoleGuard>
          } />
          <Route path="/admin/approval-chains" element={
            <RoleGuard roles={['admin']}>
              <ApprovalChains />
            </RoleGuard>
          } />
          <Route path="/admin/branding" element={
            <RoleGuard roles={['admin']}>
              <Branding />
            </RoleGuard>
          } />
          <Route path="/admin/audit" element={
            <RoleGuard roles={['admin']}>
              <AuditLog />
            </RoleGuard>
          } />
          <Route path="/admin/canned-responses" element={
            <RoleGuard roles={['admin']}>
              <CannedResponses />
            </RoleGuard>
          } />
          <Route path="/admin/sla-policies" element={
            <RoleGuard roles={['admin']}>
              <SlaPolicies />
            </RoleGuard>
          } />
        </Route>
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <TenantProvider>
        <AppInner />
      </TenantProvider>
    </ToastProvider>
  );
}
